/**
 * VoiceSuggestionModal - Modal displaying voice suggestions for a character
 * 
 * Shows a list of recommended voices with audio previews and matching reasons.
 */

import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel } from './GlassPanel'
import { Button } from './Button'
import { Icon } from './Icon'
import './VoiceSuggestionModal.css'

/**
 * VoiceCard - Individual voice suggestion card
 */
function VoiceCard({ voice, isSelected, onSelect, onPreview, isPlaying }) {
  const scorePercent = Math.round(voice.matchScore * 100)
  
  return (
    <div 
      className={`voice-card ${isSelected ? 'voice-card--selected' : ''}`}
      onClick={() => onSelect(voice)}
    >
      <div className="voice-card__header">
        <div className="voice-card__name-row">
          <span className="voice-card__name">{voice.voiceName}</span>
          <span className="voice-card__score" title={`${scorePercent}% match`}>
            {scorePercent}%
          </span>
        </div>
        <span className="voice-card__category">{voice.category}</span>
      </div>
      
      <p className="voice-card__description">{voice.description}</p>
      
      <div className="voice-card__match-reason">
        <Icon name="star" size={12} />
        <span>{voice.matchReason}</span>
      </div>
      
      {/* Labels */}
      {voice.labels && Object.keys(voice.labels).length > 0 && (
        <div className="voice-card__labels">
          {voice.labels.gender && (
            <span className="voice-card__label">{voice.labels.gender}</span>
          )}
          {voice.labels.age && (
            <span className="voice-card__label">{voice.labels.age}</span>
          )}
          {voice.labels.accent && (
            <span className="voice-card__label">{voice.labels.accent}</span>
          )}
          {voice.labels.use_case && (
            <span className="voice-card__label">{voice.labels.use_case}</span>
          )}
        </div>
      )}
      
      {/* Preview button */}
      {voice.previewUrl && (
        <button
          type="button"
          className={`voice-card__preview-btn ${isPlaying ? 'voice-card__preview-btn--playing' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            onPreview(voice)
          }}
          title={isPlaying ? 'Stop preview' : 'Play preview'}
        >
          <Icon name={isPlaying ? 'pause' : 'play'} size={14} />
          <span>{isPlaying ? 'Stop' : 'Preview'}</span>
        </button>
      )}
      
      {/* Settings preview */}
      <div className="voice-card__settings">
        <span className="voice-card__setting">
          Stability: {Math.round(voice.recommendedSettings.stability * 100)}%
        </span>
        <span className="voice-card__setting">
          Clarity: {Math.round(voice.recommendedSettings.similarityBoost * 100)}%
        </span>
      </div>
      
      {/* Selected indicator */}
      {isSelected && (
        <div className="voice-card__selected-badge">
          <Icon name="check" size={12} />
          Selected
        </div>
      )}
    </div>
  )
}

/**
 * VoiceSuggestionModal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal closes
 * @param {object} suggestions - Voice suggestions result from ElevenLabs service
 * @param {object} character - Character data for context
 * @param {function} onSelectVoice - Callback when a voice is selected
 */
export function VoiceSuggestionModal({ 
  isOpen, 
  onClose, 
  suggestions, 
  character,
  onSelectVoice 
}) {
  const [selectedVoice, setSelectedVoice] = useState(null)
  const [playingVoiceId, setPlayingVoiceId] = useState(null)
  const audioRef = useRef(null)

  const handlePreview = (voice) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }

    // If clicking the same voice that's playing, just stop
    if (playingVoiceId === voice.voiceId) {
      setPlayingVoiceId(null)
      return
    }

    // Play new audio
    if (voice.previewUrl) {
      const audio = new Audio(voice.previewUrl)
      audioRef.current = audio
      setPlayingVoiceId(voice.voiceId)
      
      audio.play().catch(err => {
        console.error('Failed to play preview:', err)
        setPlayingVoiceId(null)
      })
      
      audio.onended = () => {
        setPlayingVoiceId(null)
        audioRef.current = null
      }
    }
  }

  const handleSelect = (voice) => {
    setSelectedVoice(voice)
  }

  const handleConfirm = () => {
    if (selectedVoice) {
      onSelectVoice(selectedVoice)
    }
  }

  const handleClose = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setPlayingVoiceId(null)
    setSelectedVoice(null)
    onClose()
  }

  if (!suggestions) return null

  const voiceList = suggestions.suggestions || []

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="voice-suggestion-modal__overlay" onClick={handleClose}>
          <motion.div
            className="voice-suggestion-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <GlassPanel glow="purple" padding="lg">
              {/* Header */}
              <div className="voice-suggestion-modal__header">
                <div className="voice-suggestion-modal__title-row">
                  <span className="voice-suggestion-modal__icon">
                    <Icon name="volume" size={24} />
                  </span>
                  <div>
                    <h2 className="voice-suggestion-modal__title">Voice Suggestions</h2>
                    <p className="voice-suggestion-modal__subtitle">
                      For {character?.name || 'this character'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="voice-suggestion-modal__close"
                  onClick={handleClose}
                  title="Close"
                >
                  <Icon name="close" size={20} />
                </button>
              </div>

              {/* Criteria summary */}
              {suggestions.criteria && (
                <div className="voice-suggestion-modal__criteria">
                  <span className="voice-suggestion-modal__criteria-label">Detected traits:</span>
                  {suggestions.criteria.gender && (
                    <span className="voice-suggestion-modal__criteria-tag">
                      {suggestions.criteria.gender}
                    </span>
                  )}
                  {suggestions.criteria.age && (
                    <span className="voice-suggestion-modal__criteria-tag">
                      {suggestions.criteria.age}
                    </span>
                  )}
                  {suggestions.criteria.traits?.map(trait => (
                    <span key={trait} className="voice-suggestion-modal__criteria-tag">
                      {trait}
                    </span>
                  ))}
                  {suggestions.criteria.accentHints?.map(accent => (
                    <span key={accent} className="voice-suggestion-modal__criteria-tag">
                      {accent}
                    </span>
                  ))}
                </div>
              )}

              {/* Voice list */}
              <div className="voice-suggestion-modal__list">
                {voiceList.length === 0 ? (
                  <div className="voice-suggestion-modal__empty">
                    <Icon name="volume" size={32} />
                    <p>No matching voices found</p>
                  </div>
                ) : (
                  voiceList.map(voice => (
                    <VoiceCard
                      key={voice.voiceId}
                      voice={voice}
                      isSelected={selectedVoice?.voiceId === voice.voiceId}
                      onSelect={handleSelect}
                      onPreview={handlePreview}
                      isPlaying={playingVoiceId === voice.voiceId}
                    />
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="voice-suggestion-modal__footer">
                <span className="voice-suggestion-modal__count">
                  {voiceList.length} of {suggestions.totalVoices} voices shown
                </span>
                <div className="voice-suggestion-modal__actions">
                  <Button variant="ghost" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handleConfirm}
                    disabled={!selectedVoice}
                  >
                    Use Selected Voice
                  </Button>
                </div>
              </div>
            </GlassPanel>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default VoiceSuggestionModal
